import React from 'react'
import { Button, Checkbox, Icon, Modal, Popover, Tag, Tooltip } from 'antd'
import './mediwhoAppStyles.less'

export const rtlLayoutStyle = { direction: 'rtl' as 'rtl' }

export const mediwhoAppTableWrapStyle = { padding: '0 24px 24px' }

export const centeredHeaderCell = () => ({ style: { textAlign: 'center' as const } })

export const centeredBodyCell = () => ({
    style: { textAlign: 'center' as const, verticalAlign: 'middle' as const }
})

export function normalizeTargets(v: any): string[] {
    if (!v) return []
    if (Array.isArray(v)) return v.map(String)
    try {
        const p = JSON.parse(v)
        return Array.isArray(p) ? p.map(String) : []
    } catch (_) {
        return String(v)
            .split(/[\n,\t\r]+/)
            .map(item => item.trim())
            .filter(Boolean)
    }
}

export function SectionTitle({ label, color }: { label: string; color: string }) {
    return (
        <div className="mediwho-section-title" style={{ color, borderRight: '3px solid ' + color }}>
            {label}
        </div>
    )
}

export function StepBar({ current, steps }: { current: number; steps: Array<{ label: string; desc: string }> }) {
    return (
        <div className="mediwho-step-bar">
            {steps.map((s, i) => (
                <React.Fragment key={i}>
                    <div className="mediwho-step-item">
                        <div
                            className={
                                'mediwho-step-circle ' +
                                (i < current
                                    ? 'mediwho-step-circle-done'
                                    : i === current
                                    ? 'mediwho-step-circle-active'
                                    : 'mediwho-step-circle-pending')
                            }
                        >
                            {i < current ? '✓' : i + 1}
                        </div>
                        <div
                            className={
                                'mediwho-step-label ' +
                                (i < current
                                    ? 'mediwho-step-label-done'
                                    : i === current
                                    ? 'mediwho-step-label-active'
                                    : 'mediwho-step-label-pending')
                            }
                        >
                            {s.label}
                        </div>
                        <div className="mediwho-step-desc">{s.desc}</div>
                    </div>
                    {i < steps.length - 1 && (
                        <div
                            className={
                                'mediwho-step-connector ' +
                                (i < current ? 'mediwho-step-connector-done' : 'mediwho-step-connector-pending')
                            }
                        />
                    )}
                </React.Fragment>
            ))}
        </div>
    )
}

export const renderTruncatedValue = (value?: string | null, maxLength = 25) => {
    if (!value) return '—'
    if (value.length <= maxLength) return value
    return (
        <Tooltip title={value}>
            <span
                style={{
                    display: 'inline-block',
                    maxWidth: maxLength + 'ch',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom'
                }}
            >
                {value}
            </span>
        </Tooltip>
    )
}

export const renderLinkButton = (url: string | null | undefined, label: string) => {
    if (!url) return <span className="mediwho-link-btn-missing">—</span>
    return (
        <Tooltip title={url}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="mediwho-link-btn">
                <Icon type="link" className="mediwho-link-btn-icon" />
                {label}
            </a>
        </Tooltip>
    )
}

type HeaderProps = {
    title: string
    subtitle?: string
    actionLabel: string
    onAction: () => void
    extraActions?: React.ReactNode
}

type ActionButtonsProps = {
    editLabel?: string
    onEdit: () => void
    onDelete: () => void
    disabled?: boolean
    onResend?: () => void
}

export const renderActionButtons = ({
    editLabel = 'עריכה',
    onEdit,
    onDelete,
    disabled = false,
    onResend
}: ActionButtonsProps) => (
    <div className="mediwho-action-buttons">
        {onResend ? (
            <Button size="small" className="mediwho-action-resend-btn" onClick={onResend}>
                שליחה חוזרת
            </Button>
        ) : (
            <Button size="small" className="mediwho-action-edit-btn" disabled={disabled} onClick={onEdit}>
                {editLabel}
            </Button>
        )}
        <Button size="small" type="danger" className="mediwho-action-delete-btn" disabled={disabled} onClick={onDelete}>
            מחיקה
        </Button>
    </div>
)

export const MediwhoAppPageHeader = ({ title, subtitle, actionLabel, onAction, extraActions }: HeaderProps) => (
    <div className="mediwho-page-header">
        <div>
            <h2 className="mediwho-page-header-title">{title}</h2>
            {subtitle && <div className="mediwho-page-header-subtitle">{subtitle}</div>}
        </div>
        <div className="mediwho-page-header-actions">
            {extraActions}
            <Button type="primary" className="mediwho-page-header-action-btn" onClick={onAction}>
                {actionLabel}
            </Button>
        </div>
    </div>
)

export function confirmDelete(title: string, content: string, onOk: () => Promise<void>) {
    Modal.confirm({
        title,
        content,
        okText: 'מחק',
        okType: 'danger',
        cancelText: 'ביטול',
        okButtonProps: { className: 'mediwho-confirm-ok-btn' },
        cancelButtonProps: { className: 'mediwho-confirm-cancel-btn' },
        onOk
    })
}

type WizardFooterProps = {
    step: number
    lastStep: number
    saving: boolean
    onNext: () => void
    onBack: () => void
    onSave: () => void
    onCancel: () => void
    saveLabel?: string
    // form + required fields per step — used to disable Next/Save when fields are empty or have errors
    form?: any
    requiredFieldsByStep?: string[][]
    extraDisabled?: boolean
}

// Shared wizard step machinery — eliminates the snapshotValues/handleNext/handleBack/iv copy-paste
// across BenefitModal, LectureModal, NotificationModal.
export type WizardSetState = (updater: (s: any) => any) => void
export type WizardGetState = () => { step: number; savedValues: Record<string, any> }

export function makeWizardHandlers(
    form: any,
    stepFields: string[][],
    getState: WizardGetState,
    setState: WizardSetState
) {
    const snapshot = () => {
        const current = form.getFieldsValue()
        setState(s => ({ savedValues: Object.assign({}, s.savedValues, current) }))
    }
    const getSavedFieldValue = (field: string, fallback: any) => {
        const { savedValues } = getState()
        return field in savedValues ? savedValues[field] : fallback
    }
    const handleNext = () => {
        const { step } = getState()
        const fields = stepFields[step]
        if (!fields || fields.length === 0) {
            snapshot()
            setState(s => ({ step: s.step + 1 }))
            return
        }
        form.validateFields(fields, (err: any) => {
            if (err) return
            snapshot()
            setState(s => ({ step: s.step + 1 }))
        })
    }
    const handleBack = () => {
        snapshot()
        setState(s => ({ step: s.step - 1 }))
        // rc-form drops a field's live value when it unmounts, so re-apply
        // savedValues once the previous step's inputs are back in the DOM.
        setTimeout(() => form.setFieldsValue(getState().savedValues))
    }
    return { snapshot, getSavedFieldValue, handleNext, handleBack }
}

// --- Shared target popover cell (used in Benefits and Notifications tables) ---

const POPOVER_PREVIEW = 5

interface TargetPopoverCellProps {
    targetType: string
    targetValue: any // raw value from the row (array or JSON string)
    targetMeta: Record<string, { label: string; color: string }>
    onShowAll?: () => void // optional "show all" button for remaining items
}

export class TargetPopoverCell extends React.Component<TargetPopoverCellProps, { open: boolean }> {
    state = { open: false }

    render() {
        const { targetType, targetValue, targetMeta, onShowAll } = this.props
        const meta = targetMeta[targetType] || { label: targetType, color: 'default' }
        const values = normalizeTargets(targetValue)

        if (targetType === 'all' || values.length === 0) {
            return (
                <div style={{ textAlign: 'center' }}>
                    <Tag color={meta.color}>{meta.label}</Tag>
                </div>
            )
        }

        const isRtl = targetType !== 'emails'
        const preview = values.slice(0, POPOVER_PREVIEW)
        const remaining = values.length - preview.length

        const content = (
            <div style={{ direction: isRtl ? 'rtl' : 'ltr', textAlign: isRtl ? 'right' : 'left' }}>
                {preview.map((v, i) => (
                    <div key={i} style={{ padding: '2px 0', fontFamily: 'monospace', fontSize: 13 }}>
                        {String(v)}
                    </div>
                ))}
                {remaining > 0 &&
                    (onShowAll ? (
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, marginTop: 4 }}
                            onClick={() => {
                                this.setState({ open: false })
                                onShowAll()
                            }}
                        >
                            {'ועוד ' + remaining + ' נוספים...'}
                        </Button>
                    ) : (
                        <div style={{ marginTop: 4, color: '#999', fontSize: 12 }}>ועוד {remaining} נוספים...</div>
                    ))}
            </div>
        )

        return (
            <div style={{ textAlign: 'center' }}>
                <Popover
                    visible={this.state.open}
                    onVisibleChange={v => this.setState({ open: v })}
                    title={
                        <span style={{ display: 'block', direction: 'rtl', textAlign: 'right' }}>
                            {meta.label + ' (' + values.length + ')'}
                        </span>
                    }
                    content={content}
                    trigger="click"
                    placement="left"
                >
                    <Tag color={meta.color} style={{ cursor: 'pointer' }}>
                        {meta.label} ({values.length})
                    </Tag>
                </Popover>
            </div>
        )
    }
}

// --- Shared send-notification checkbox (used in BenefitModal and LectureModal) ---

interface SendNotificationCheckboxProps {
    checked: boolean
    onChange: (checked: boolean) => void
    className?: string
}

export function SendNotificationCheckbox({ checked, onChange, className }: SendNotificationCheckboxProps) {
    return (
        <div
            className="mediwho-send-notification-wrap"
            style={{ borderTop: '1px dashed #e8e8e8', paddingTop: 12, marginTop: 4 }}
        >
            <Checkbox
                className={'mediwho-send-notification-checkbox ' + (className || '')}
                checked={checked}
                onChange={e => onChange(e.target.checked)}
            >
                <span style={{ fontWeight: 600, color: '#0958d9' }}>שלח התראה למשתמשים לאחר השמירה</span>
            </Checkbox>
            {checked && (
                <p style={{ textAlign: 'right', margin: '4px 0 0 24px', fontSize: 12, color: '#8c8c8c' }}>
                    לאחר השמירה יפתח חלון לניסוח ההתראה — תוכל לבחור למי לשלוח ומתי
                </p>
            )}
        </div>
    )
}

export function renderWizardFooter({
    step,
    lastStep,
    saving,
    onNext,
    onBack,
    onSave,
    onCancel,
    saveLabel = 'שמירה',
    form,
    requiredFieldsByStep,
    extraDisabled = false
}: WizardFooterProps): React.ReactNode[] {
    let disabled = false
    if (form) {
        const errors = form.getFieldsError()
        const hasErrors = Object.values(errors).some((fieldErrors: any) => {
            return Array.isArray(fieldErrors) ? fieldErrors.length > 0 : !!fieldErrors
        })
        const stepRequired: string[] = (requiredFieldsByStep && requiredFieldsByStep[step]) || []
        const vals = form.getFieldsValue(stepRequired)
        const missingRequired = stepRequired.some(f => {
            const v = vals[f]
            return v === undefined || v === null || (typeof v === 'string' ? v.trim() === '' : v === '')
        })
        disabled = hasErrors || missingRequired
    }
    disabled = disabled || extraDisabled
    const footer: React.ReactNode[] = []
    if (step < lastStep) {
        footer.push(
            <Button key="next" type="primary" className="mediwho-wizard-next-btn" disabled={disabled} onClick={onNext}>
                <span style={{ marginRight: 4 }}>‹</span> הבא
            </Button>
        )
    } else {
        footer.push(
            <Button
                key="save"
                type="primary"
                className="mediwho-wizard-save-btn"
                loading={saving}
                disabled={disabled}
                onClick={onSave}
            >
                {saveLabel}
            </Button>
        )
    }
    footer.push(
        <Button key="cancel" className="mediwho-wizard-cancel-btn" onClick={onCancel}>
            ביטול
        </Button>
    )
    if (step > 0) {
        footer.push(
            <Button key="back" className="mediwho-wizard-back-btn" onClick={onBack}>
                חזרה <span style={{ marginLeft: 4 }}>›</span>
            </Button>
        )
    }
    return footer
}
